class BLSCartExtras extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';

    this.bindCarousel();
    this.observeDrawer();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.stopAutoPlay();
    clearTimeout(this.resumeTimer);
    this.drawerObserver?.disconnect();

    if (this.visibilityHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityHandler
      );
    }
  }

  async loadRecommendations() {
    const productId = this.dataset.productId;
    const section = this.querySelector(
      '[data-recommendations]'
    );
    const track = this.querySelector(
      '[data-recommendations-track]'
    );

    if (!productId || !section || !track) return;

    const root =
      window.Shopify?.routes?.root || '/';

    const url = new URL(
      `${root}recommendations/products.json`,
      window.location.origin
    );

    url.searchParams.set(
      'product_id',
      productId
    );

    url.searchParams.set(
      'limit',
      '6'
    );

    url.searchParams.set(
      'intent',
      'related'
    );

    try {
      const response = await fetch(
        url.toString(),
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) return;

      const data =
        await response.json();

      const cartProductIds =
        new Set(
          (
            this.dataset
              .cartProductIds || ''
          )
            .split(',')
            .map((id) =>
              id.trim()
            )
            .filter(Boolean)
        );

      const products = (
        data.products || []
      ).filter(
        (product) =>
          !cartProductIds.has(
            String(product.id)
          )
      );

      /*
       * 如果 Shopify API 没有返回推荐商品，
       * 保留 Liquid fallback 商品，不清空。
       */
      if (products.length === 0) {
        this.updateCarouselState();
        return;
      }

      const fragment =
        document.createDocumentFragment();

      products.forEach(
        (product) => {
          fragment.appendChild(
            this.createProductCard(
              product
            )
          );
        }
      );

      track.replaceChildren(
        fragment
      );

      section.hidden = false;

      this.updateCarouselState();
      this.restartAutoPlay();
    } catch (error) {
      console.error(
        'BLS cart recommendations:',
        error
      );

      this.updateCarouselState();
    }
  }

  createProductCard(product) {
    const link =
      document.createElement('a');

    link.className =
      'bls-cart-rec__card';

    link.href =
      product.url ||
      `${
        window.Shopify?.routes
          ?.root || '/'
      }products/${product.handle}`;

    const media =
      document.createElement('div');

    media.className =
      'bls-cart-rec__media';

    const imageUrl =
      product.featured_image ||
      product.images?.[0];

    if (imageUrl) {
      const image =
        document.createElement(
          'img'
        );

      image.src = imageUrl;

      image.alt =
        product.title || '';

      image.loading = 'lazy';

      image.width = 360;
      image.height = 360;

      media.appendChild(image);
    }

    const title =
      document.createElement(
        'div'
      );

    title.className =
      'bls-cart-rec__title';

    title.textContent =
      product.title || '';

    const price =
      document.createElement(
        'div'
      );

    price.className =
      'bls-cart-rec__price';

    const amount =
      product.price_min ??
      product.price ??
      0;

    // Match the price shown on this card with the same variant's original
    // Compare-at price; never divide unrelated product min/max prices.
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const cardVariant = variants.find(
      (variant) => Number(variant.price) === Number(amount)
    );
    const sellingPrice = Number(amount);
    const originalPrice = Number(cardVariant?.compare_at_price || 0);

    if (
      sellingPrice >= 0 &&
      Number.isFinite(sellingPrice) &&
      Number.isFinite(originalPrice) &&
      originalPrice > sellingPrice
    ) {
      const percentage = Math.round(
        ((originalPrice - sellingPrice) / originalPrice) * 100
      );
      if (percentage > 0) {
        const badge = document.createElement('span');
        badge.className = 'bls-discount-badge';
        badge.textContent = `-${percentage}%`;
        badge.setAttribute(
          'aria-label',
          `${percentage} percent off original price`
        );
        media.appendChild(badge);
      }
    }

    const formatted =
      this.formatMoney(amount);

    price.textContent =
      product.price_varies
        ? `From ${formatted}`
        : formatted;

    link.append(
      media,
      title,
      price
    );

    return link;
  }

  formatMoney(cents) {
    const currency =
      this.dataset.currency ||
      'USD';

    const locale =
      this.dataset.locale ||
      'en-US';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency,
        }
      ).format(
        Number(cents) / 100
      );
    } catch (error) {
      return `$${(
        Number(cents) / 100
      ).toFixed(2)}`;
    }
  }

  bindCarousel() {
    const track =
      this.querySelector(
        '[data-recommendations-track]'
      );

    const prev =
      this.querySelector(
        '[data-recommendations-prev]'
      );

    const next =
      this.querySelector(
        '[data-recommendations-next]'
      );

    if (!track) return;

    const scroll = (
      direction
    ) => {
      this.stopAutoPlay();

      this.scrollOneCard(
        direction
      );

      this.scheduleAutoPlay();
    };

    prev?.addEventListener(
      'click',
      () => scroll(-1)
    );

    next?.addEventListener(
      'click',
      () => scroll(1)
    );

    /*
     * Desktop:
     * 鼠标放到产品上时暂停。
     */
    track.addEventListener(
      'mouseenter',
      () =>
        this.stopAutoPlay()
    );

    track.addEventListener(
      'mouseleave',
      () =>
        this.scheduleAutoPlay()
    );

    /*
     * Keyboard accessibility
     */
    track.addEventListener(
      'focusin',
      () =>
        this.stopAutoPlay()
    );

    track.addEventListener(
      'focusout',
      () =>
        this.scheduleAutoPlay()
    );

    /*
     * Mobile touch / drag
     */
    track.addEventListener(
      'pointerdown',
      () =>
        this.stopAutoPlay()
    );

    track.addEventListener(
      'pointerup',
      () =>
        this.scheduleAutoPlay()
    );

    track.addEventListener(
      'pointercancel',
      () =>
        this.scheduleAutoPlay()
    );

    /*
     * 浏览器切换到后台时停止。
     */
    this.visibilityHandler =
      () => {
        if (document.hidden) {
          this.stopAutoPlay();
        } else {
          this.scheduleAutoPlay(
            800
          );
        }
      };

    document.addEventListener(
      'visibilitychange',
      this.visibilityHandler
    );

    this.updateCarouselState();
  }

  scrollOneCard(direction) {
    const track =
      this.querySelector(
        '[data-recommendations-track]'
      );

    const card =
      track?.querySelector(
        '.bls-cart-rec__card'
      );

    if (!track || !card) return;

    const styles =
      getComputedStyle(track);

    const gap =
      parseFloat(
        styles.columnGap ||
          styles.gap ||
          '0'
      );

    const distance =
      card.getBoundingClientRect()
        .width + gap;

    track.scrollBy({
      left:
        direction *
        distance,

      behavior: 'smooth',
    });
  }

  autoAdvance() {
    const track =
      this.querySelector(
        '[data-recommendations-track]'
      );

    const cards =
      track?.querySelectorAll(
        '.bls-cart-rec__card'
      ) || [];

    /*
     * 一屏显示两个。
     * 如果只有两个商品，就不需要轮播。
     */
    if (
      !track ||
      cards.length <= 2
    ) {
      return;
    }

    const maxScroll =
      Math.max(
        0,
        track.scrollWidth -
          track.clientWidth
      );

    const atEnd =
      track.scrollLeft >=
      maxScroll - 4;

    /*
     * 到最后自动回到第一组。
     */
    if (atEnd) {
      track.scrollTo({
        left: 0,
        behavior: 'smooth',
      });

      return;
    }

    this.scrollOneCard(1);
  }

  startAutoPlay() {
    this.stopAutoPlay();

    /*
     * 尊重用户的 Reduce Motion 设置。
     */
    const reduceMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    const track =
      this.querySelector(
        '[data-recommendations-track]'
      );

    const cards =
      track?.querySelectorAll(
        '.bls-cart-rec__card'
      ) || [];

    const dialog =
      this.closest('dialog');

    if (
      reduceMotion ||
      cards.length <= 2 ||
      document.hidden
    ) {
      return;
    }

    /*
     * Cart drawer 没打开时不运行。
     */
    if (
      dialog &&
      !dialog.open
    ) {
      return;
    }

    /*
     * 自动轮播速度：
     * 3500 = 3.5 秒
     */
    this.autoTimer =
      window.setInterval(
        () => {
          this.autoAdvance();
        },
        3500
      );
  }

  stopAutoPlay() {
    if (this.autoTimer) {
      clearInterval(
        this.autoTimer
      );

      this.autoTimer = null;
    }
  }

  scheduleAutoPlay(
    delay = 2200
  ) {
    clearTimeout(
      this.resumeTimer
    );

    this.resumeTimer =
      window.setTimeout(
        () => {
          this.startAutoPlay();
        },
        delay
      );
  }

  restartAutoPlay() {
    this.stopAutoPlay();

    this.scheduleAutoPlay(
      900
    );
  }

  updateCarouselState() {
    const track =
      this.querySelector(
        '[data-recommendations-track]'
      );

    const controls =
      this.querySelector(
        '[data-recommendations-controls]'
      );

    const cards =
      track?.querySelectorAll(
        '.bls-cart-rec__card'
      ) || [];

    /*
     * 超过 2 个商品才显示箭头。
     */
    if (controls) {
      controls.hidden =
        cards.length <= 2;
    }

    if (
      cards.length > 2
    ) {
      this.restartAutoPlay();
    } else {
      this.stopAutoPlay();
    }
  }

  observeDrawer() {
    const dialog =
      this.closest('dialog');

    if (!dialog) {
      this.scheduleAutoPlay(
        900
      );

      return;
    }

    /*
     * 监听 Cart Drawer 开关。
     */
    this.drawerObserver =
      new MutationObserver(
        () => {
          if (dialog.open) {
            this.scheduleAutoPlay(
              700
            );
          } else {
            this.stopAutoPlay();
          }
        }
      );

    this.drawerObserver.observe(
      dialog,
      {
        attributes: true,
        attributeFilter: [
          'open',
        ],
      }
    );

    if (dialog.open) {
      this.scheduleAutoPlay(
        700
      );
    }
  }
}

if (
  !customElements.get(
    'bls-cart-extras'
  )
) {
  customElements.define(
    'bls-cart-extras',
    BLSCartExtras
  );
}